import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Accounts, Deals } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { eur, fmtDay } from "@/lib/format";
import { stageColor } from "@/lib/colors";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { Segmented } from "@/components/ui";
import RelationshipMap from "@/components/RelationshipMap";
import { AccountContracts } from "@/components/contracts/LinkedContracts";
import AccountDemos from "@/components/demos/AccountDemos";
import { confirmDialog, toast } from "@/lib/feedback";

export default function AccountDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user)?.role === "admin";
  const openDeal = useUIStore((s) => s.openDeal);
  const [tab, setTab] = useState<"deals" | "people" | "contracts" | "demos">("deals");

  const accountQuery = useQuery({
    queryKey: ["account", id],
    queryFn: () => Accounts.get(id),
    enabled: !!id,
  });
  // Deals are already RLS-scoped to what the caller can see; filter client-side
  // since GET /api/accounts/{id} returns no nested deals.
  const dealsQuery = useQuery({ queryKey: ["deals"], queryFn: () => Deals.list() });

  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const account = accountQuery.data;

  useEffect(() => {
    setName(account?.name ?? "");
    setNotes(account?.notes ?? "");
  }, [account]);

  const saveMutation = useMutation({
    mutationFn: (payload: { name?: string; notes?: string | null }) => Accounts.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", id] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => toast.err(`Save failed: ${apiErrorMessage(e)}`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => Accounts.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      navigate("/accounts");
    },
    onError: (e) => toast.err(`Delete failed: ${apiErrorMessage(e)}`),
  });

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/accounts"));

  const deals = (dealsQuery.data ?? []).filter((d) => d.account_id === id);

  return (
    <div className="px-4 py-4 md:px-6 md:py-6">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn btn-ghost" onClick={goBack} title="Back" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-mut-300">Account</div>
            <h1 className="text-lg font-semibold truncate">{account?.name ?? "—"}</h1>
          </div>
        </div>
        {account && isAdmin && (
          <button
            className="btn btn-ghost text-accent-pink"
            onClick={async () => {
              if (
                await confirmDialog({
                  title: "Delete this account?",
                  body: "Its deals stay, but they'll no longer belong to an account.",
                  confirmLabel: "Delete account",
                  danger: true,
                })
              )
                deleteMutation.mutate();
            }}
            title="Delete"
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        )}
      </header>

      {accountQuery.isLoading ? (
        <div className="text-mut-300">Loading…</div>
      ) : !account ? (
        <div className="text-mut-300">
          Account not found.{" "}
          <button className="underline" onClick={goBack}>
            Go back
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Deals list / relationship map */}
          <div className="lg:col-span-2 min-w-0 space-y-4">
            <Segmented
              label="Account view"
              value={tab}
              onChange={setTab}
              options={[
                { value: "deals", label: `Deals (${deals.length})` },
                { value: "people", label: "People" },
                { value: "contracts", label: "Contracts" },
                { value: "demos", label: "Demos" },
              ]}
            />
            {tab === "contracts" ? (
              <AccountContracts accountId={id} />
            ) : tab === "demos" ? (
              <AccountDemos accountId={id} />
            ) : tab === "people" ? (
              <RelationshipMap accountId={id} accountName={account.name} />
            ) : (
              <div className="panel overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-fg/[0.02] border-b border-fg/[0.05]">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                        Company
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                        Stage
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                        Owner
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-mut-300">
                        ARR
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((d) => {
                      const color = stageColor(d.stage);
                      return (
                        <tr
                          key={d.id}
                          className="border-t border-fg/[0.04] hover:bg-fg/[0.02] cursor-pointer"
                          onClick={() => openDeal(d.id)}
                        >
                          <td className="px-3 py-2.5 font-medium">{d.lead}</td>
                          <td className="px-3 py-2.5">
                            <span
                              className="chip"
                              style={{ backgroundColor: stageColor(d.stage, null, 0.12), color }}
                            >
                              {d.stage}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-mut-300">{d.primary_owner || "—"}</td>
                          <td className="px-3 py-2.5 text-right">{eur(d.arr, { short: true })}</td>
                        </tr>
                      );
                    })}
                    {deals.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-10 text-center text-mut-300">
                          No deals linked to this account.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Account info */}
          <aside className="lg:col-span-1 space-y-6">
            <div className="panel p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="panel-tight p-3">
                  <div className="label">Deals</div>
                  <div className="font-semibold">{account.deal_count}</div>
                </div>
                <div className="panel-tight p-3">
                  <div className="label">Created</div>
                  <div className="font-semibold text-md">{fmtDay(account.created_at)}</div>
                </div>
              </div>

              <div>
                <label className="label">Name</label>
                <input
                  className={`input${isAdmin ? "" : " disabled:opacity-60 disabled:cursor-not-allowed"}`}
                  disabled={!isAdmin}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    const v = name.trim();
                    if (isAdmin && v && v !== account.name) saveMutation.mutate({ name: v });
                  }}
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea
                  className={`input min-h-[100px]${isAdmin ? "" : " disabled:opacity-60 disabled:cursor-not-allowed"}`}
                  disabled={!isAdmin}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() => {
                    const v = notes.trim() || null;
                    if (isAdmin && v !== (account.notes ?? null)) saveMutation.mutate({ notes: v });
                  }}
                />
              </div>

              <div className="text-xs text-mut-300 pt-2 border-t border-fg/[0.05]">
                Updated {fmtDay(account.updated_at)}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
